/**
 * Routes of the standalone web (docs/13 §4). They hang from a pathless layout so `/guest`, `/tv`,
 * `/app` and `/auth/action` keep working exactly as before: the TV stick depends on them.
 */
import { type AnyRoute, createRoute, lazyRouteComponent } from '@tanstack/react-router';

import { WebLayout } from './ui/WebLayout';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** `none` hides the nav, tab bar and footer (the player, full-screen flows). */
    chrome?: 'none';
    /** Hides only the footer. */
    footer?: false;
    /** MW-05, ME-10: a whole screen on a phone, under no header. */
    mobileHeader?: false;
  }
}

/** `?q=` of `/buscar`. */
function searchSearch(search: Record<string, unknown>): { q?: string } {
  return typeof search.q === 'string' && search.q ? { q: search.q } : {};
}

/** `?song=&tone=` of `/cantar`. */
function singSearch(search: Record<string, unknown>): { song?: string; tone?: string } {
  return {
    ...(typeof search.song === 'string' && search.song ? { song: search.song } : {}),
    ...(typeof search.tone === 'string' && search.tone ? { tone: search.tone } : {}),
  };
}

/** `?checkout=success|cancel`, on whatever page Stripe returned to (§5.8). */
export function checkoutSearch(search: Record<string, unknown>): { checkout?: string } {
  return typeof search.checkout === 'string' ? { checkout: search.checkout } : {};
}

export function webRoutes(rootRoute: AnyRoute) {
  const layout = createRoute({
    getParentRoute: () => rootRoute,
    id: 'web',
    component: WebLayout,
    validateSearch: checkoutSearch,
  });

  const home = createRoute({
    getParentRoute: () => layout,
    path: '/',
    component: lazyRouteComponent(() => import('./pages/Home'), 'Home'),
  });

  const landing = createRoute({
    getParentRoute: () => layout,
    path: '/cancion/$slug',
    component: lazyRouteComponent(() => import('./pages/Landing'), 'Landing'),
  });

  const search = createRoute({
    getParentRoute: () => layout,
    path: '/buscar',
    validateSearch: searchSearch,
    staticData: { footer: false },
    component: lazyRouteComponent(() => import('./pages/Search'), 'Search'),
  });

  const playlist = createRoute({
    getParentRoute: () => layout,
    path: '/lista/$id',
    staticData: { footer: false },
    component: lazyRouteComponent(() => import('./pages/ListPages'), 'PlaylistPage'),
  });

  const free = createRoute({
    getParentRoute: () => layout,
    path: '/gratis',
    staticData: { footer: false },
    component: lazyRouteComponent(() => import('./pages/ListPages'), 'FreePage'),
  });

  const artist = createRoute({
    getParentRoute: () => layout,
    path: '/artista/$id',
    staticData: { footer: false },
    component: lazyRouteComponent(() => import('./pages/ListPages'), 'ArtistPage'),
  });

  const plans = createRoute({
    getParentRoute: () => layout,
    path: '/planes',
    staticData: { footer: false },
    component: lazyRouteComponent(() => import('./pages/Plans'), 'Plans'),
  });

  const myMusic = createRoute({
    getParentRoute: () => layout,
    path: '/mi-musica',
    component: lazyRouteComponent(() => import('./pages/MyMusic'), 'Favorites'),
  });

  const myRecent = createRoute({
    getParentRoute: () => layout,
    path: '/mi-musica/recientes',
    component: lazyRouteComponent(() => import('./pages/MyMusic'), 'Recent'),
  });

  const account = createRoute({
    getParentRoute: () => layout,
    path: '/cuenta',
    component: lazyRouteComponent(() => import('./pages/Account'), 'Account'),
  });

  const sing = createRoute({
    getParentRoute: () => layout,
    path: '/cantar',
    validateSearch: singSearch,
    staticData: { chrome: 'none' },
    component: lazyRouteComponent(() => import('./pages/Sing'), 'Sing'),
  });

  const party = createRoute({
    getParentRoute: () => layout,
    path: '/fiesta',
    staticData: { footer: false },
    component: lazyRouteComponent(() => import('./pages/Party'), 'Party'),
  });

  const partyJoin = createRoute({
    getParentRoute: () => layout,
    path: '/fiesta/unirse',
    staticData: { mobileHeader: false },
    component: lazyRouteComponent(() => import('./pages/Join'), 'PartyJoin'),
  });

  const joinCode = createRoute({
    getParentRoute: () => layout,
    path: '/j/$code',
    staticData: { chrome: 'none' },
    component: lazyRouteComponent(() => import('./pages/Join'), 'JoinByCode'),
  });

  return layout.addChildren([
    home,
    landing,
    search,
    playlist,
    free,
    artist,
    plans,
    myMusic,
    myRecent,
    account,
    sing,
    party,
    partyJoin,
    joinCode,
  ]);
}
